/**
 * HS 분류 이력 + 정정 통계 dashboard.
 *
 * - /api/hs-classify/recent — 최근 분류 이력
 * - /api/hs-classify/stats — chapter 별 사용/정정 통계 (30 days)
 */

import React, { useEffect, useState } from 'react';
import { BarChart2, RefreshCw, ExternalLink, Edit3, CheckCircle, AlertCircle, User } from 'lucide-react';

const CHAPTER_LABELS = {
  '33': 'K-Beauty', '39': 'Plastics', '42': 'Leather',
  '61': 'Textile-Knit', '62': 'Textile-Woven',
  '64': 'Footwear', '84': 'Machinery', '85': 'Electrical',
  '86': 'Railway', '90': 'Optical/Medical', '94': 'Furniture',
};

const CONF_COLOR = {
  high: 'bg-emerald-100 text-emerald-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-rose-100 text-rose-800',
};

export default function HSClassifierAuditPanel({ apiBaseUrl = '' }) {
  const [stats, setStats] = useState([]);
  const [rows, setRows] = useState([]);
  const [userMap, setUserMap] = useState({}); // USER_ID → USER_NAME (D_DOCUAI_USER, MSSQL)
  const [chapterFilter, setChapterFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 관리자 인증(401/403) — admin_token 은 12시간 만료 JWT. 만료를 조용히 '이력 없음'으로
  // 삼키지 않고 그 자리에서 재인증(비밀번호 → /api/auth/admin-login).
  const [authNeeded, setAuthNeeded] = useState(false);
  const [adminPw, setAdminPw] = useState('');
  const [adminAuthBusy, setAdminAuthBusy] = useState(false);
  const [adminAuthErr, setAdminAuthErr] = useState('');
  // review 정정 UI
  const [reviewId, setReviewId] = useState(null);
  const [reviewHsk, setReviewHsk] = useState('');
  const [reviewNote, setReviewNote] = useState('');

  async function fetchAll() {
    setLoading(true);
    setError(null);
    // recent는 user_id(사용자 식별)를 반환하므로 Spring에서 어드민 인증 필요(authenticateAdmin).
    // docuai2 레퍼런스와 동일하게 admin_token을 stats·recent 양쪽에 첨부(없으면 미첨부 → 401/빈목록).
    const adminToken = localStorage.getItem('admin_token');
    const authHeaders = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      const [sRes, rRes, uRes] = await Promise.all([
        fetch(`${apiBaseUrl}/api/hs-classify/stats`, { headers: authHeaders }),
        fetch(`${apiBaseUrl}/api/hs-classify/recent?limit=100${chapterFilter ? `&chapter=${chapterFilter}` : ''}`, {
          headers: authHeaders,
        }),
        // 사용자 이름 매핑: audit.user_id(PG)와 D_DOCUAI_USER(MSSQL)는 다른 DB라 조인 불가 → 목록 받아 프론트 매핑.
        fetch(`${apiBaseUrl}/api/admin/users`, { headers: authHeaders }).catch(() => null),
      ]);
      const s = await sRes.json().catch(() => ({}));
      setStats(s.chapters || []);
      // recent 는 user_id 노출 때문에 관리자 JWT 필수 — 401/403 을 '이력 없음'으로
      // 위장하지 않고 재인증 UI 로 전환.
      if (rRes.status === 401 || rRes.status === 403) {
        setAuthNeeded(true);
        setRows([]);
      } else {
        const r = await rRes.json().catch(() => ({}));
        setAuthNeeded(false);
        setRows(r.rows || []);
      }
      const u = uRes && uRes.ok ? await uRes.json().catch(() => null) : null;
      if (u && Array.isArray(u.data)) {
        const m = {};
        u.data.forEach((usr) => { if (usr.USER_ID) m[usr.USER_ID] = usr.USER_NAME; });
        setUserMap(m);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [chapterFilter]);

  async function submitReview(id) {
    if (!/^\d{10}$/.test(reviewHsk)) {
      alert('HSK 10자리 (숫자만)');
      return;
    }
    try {
      const r = await fetch(`${apiBaseUrl}/api/hs-classify/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audit_id: id, reviewed_hsk: reviewHsk, review_note: reviewNote || null }),
      });
      if (!r.ok) {
        const err = await r.json();
        alert('실패: ' + (err.detail || err.error));
      } else {
        setReviewId(null);
        setReviewHsk('');
        setReviewNote('');
        fetchAll();
      }
    } catch (err) {
      alert(err.message);
    }
  }

  const totalRequests = stats.reduce((s, r) => s + parseInt(r.total || 0), 0);
  const totalReviewed = stats.reduce((s, r) => s + parseInt(r.reviewed_count || 0), 0);
  const totalCorrected = stats.reduce((s, r) => s + parseInt(r.reviewer_corrected || 0), 0);

  return (
    <div className="h-full overflow-auto bg-[#F5F1EC] p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <BarChart2 className="w-7 h-7 text-blue-600" />
            <h1 className="text-2xl font-semibold text-gray-900">HS 분류 이력 + 통계</h1>
          </div>
          <button onClick={fetchAll} className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            새로고침
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-rose-50 border border-rose-200 rounded p-3 flex items-start gap-2 text-sm text-rose-800">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>{error}</div>
          </div>
        )}

        {/* 종합 통계 */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <StatCard label="총 분류 (30일)" value={totalRequests} />
          <StatCard label="관세사 검토" value={totalReviewed} />
          <StatCard label="정정 발생" value={totalCorrected} accent={totalCorrected > 0 ? 'text-rose-600' : undefined} />
        </div>

        {/* Chapter 별 통계 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-5">
          <div className="text-sm font-semibold text-gray-900 mb-2">Chapter 별 사용/정정 (최근 30일)</div>
          <table className="w-full text-xs">
            <thead className="text-left text-gray-500 border-b border-gray-200">
              <tr>
                <th className="py-1.5 pr-2">Chapter</th>
                <th className="py-1.5 pr-2 text-right">총</th>
                <th className="py-1.5 pr-2 text-right">avg ms</th>
                <th className="py-1.5 pr-2 text-right">high</th>
                <th className="py-1.5 pr-2 text-right">medium</th>
                <th className="py-1.5 pr-2 text-right">low</th>
                <th className="py-1.5 pr-2 text-right">review</th>
                <th className="py-1.5 pr-2 text-right">정정</th>
                <th className="py-1.5 text-right">정정율</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((c) => {
                const corrRate = parseInt(c.reviewed_count) > 0 ? (parseInt(c.reviewer_corrected) / parseInt(c.reviewed_count) * 100).toFixed(0) : '-';
                return (
                  <tr key={c.request_chapter} className="border-b border-gray-100 last:border-0">
                    <td className="py-1 pr-2 font-mono">
                      <span className="cursor-pointer hover:text-blue-600" onClick={() => setChapterFilter(c.request_chapter)}>
                        {c.request_chapter}류
                      </span>
                      <span className="ml-1.5 text-gray-500">{CHAPTER_LABELS[c.request_chapter] || ''}</span>
                    </td>
                    <td className="py-1 pr-2 text-right font-mono">{c.total}</td>
                    <td className="py-1 pr-2 text-right font-mono">{c.avg_response_ms}</td>
                    <td className="py-1 pr-2 text-right font-mono text-emerald-700">{c.high_conf}</td>
                    <td className="py-1 pr-2 text-right font-mono text-amber-700">{c.medium_conf}</td>
                    <td className="py-1 pr-2 text-right font-mono text-rose-700">{c.low_conf}</td>
                    <td className="py-1 pr-2 text-right font-mono">{c.review_count}</td>
                    <td className="py-1 pr-2 text-right font-mono">{c.reviewer_corrected}</td>
                    <td className="py-1 text-right font-mono">{corrRate}{corrRate !== '-' && '%'}</td>
                  </tr>
                );
              })}
              {stats.length === 0 && (
                <tr><td colSpan={9} className="py-3 text-center text-gray-400">데이터 없음</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 최근 이력 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-gray-900">최근 분류 이력 ({rows.length})</div>
            {chapterFilter && (
              <button onClick={() => setChapterFilter('')} className="text-xs text-blue-600 hover:underline">
                전체 보기 (현재: {chapterFilter}류)
              </button>
            )}
          </div>
          <div className="space-y-1.5 max-h-[60vh] overflow-auto">
            {rows.map((r) => (
              <div key={r.id} className="border border-gray-200 rounded p-2.5 hover:border-gray-300">
                <div className="flex items-center justify-between mb-1 gap-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="text-xs text-gray-500 font-mono">#{r.id}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${CONF_COLOR[r.confidence] || 'bg-gray-100 text-gray-700'}`}>
                      {r.confidence}
                    </span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                      {r.request_chapter}류
                    </span>
                    <span className="font-mono text-sm">{r.predicted_hsk}</span>
                    {r.reviewed_hsk && r.reviewed_hsk !== r.predicted_hsk && (
                      <>
                        <span className="text-xs text-gray-500">→</span>
                        <span className="font-mono text-sm text-rose-700">{r.reviewed_hsk}</span>
                        <span className="text-xs text-rose-700">(정정)</span>
                      </>
                    )}
                    {r.reviewed_hsk && r.reviewed_hsk === r.predicted_hsk && (
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                    )}
                  </div>
                  <div className="text-xs text-gray-400 whitespace-nowrap">
                    {new Date(r.requested_at).toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    <span className="ml-2">{r.response_time_ms}ms</span>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs text-gray-700 truncate flex-1" title={r.line_item}>{r.line_item}</div>
                  <div className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap" title={r.user_id ? `ID: ${r.user_id}` : '분류한 사용자'}>
                    <User className="w-3 h-3 text-gray-400" />
                    {userMap[r.user_id] || r.user_id || '—'}
                  </div>
                </div>

                {reviewId === r.id ? (
                  <div className="mt-2 flex items-center gap-1.5 text-xs">
                    <input
                      type="text"
                      value={reviewHsk}
                      onChange={(e) => setReviewHsk(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="HSK 10자리"
                      className="w-32 font-mono rounded border border-amber-300 px-1.5 py-0.5"
                    />
                    <input
                      type="text"
                      value={reviewNote}
                      onChange={(e) => setReviewNote(e.target.value)}
                      placeholder="메모"
                      className="flex-1 rounded border border-amber-300 px-1.5 py-0.5"
                    />
                    <button onClick={() => submitReview(r.id)} className="px-2 py-0.5 rounded bg-amber-600 text-white">저장</button>
                    <button onClick={() => { setReviewId(null); setReviewHsk(''); }} className="px-2 py-0.5 rounded border border-gray-300">취소</button>
                  </div>
                ) : (
                  !r.reviewed_hsk && (
                    <button
                      onClick={() => { setReviewId(r.id); setReviewHsk(r.predicted_hsk || ''); }}
                      className="mt-1 text-xs text-blue-600 hover:underline flex items-center gap-0.5"
                    >
                      <Edit3 className="w-3 h-3" />
                      정정
                    </button>
                  )
                )}
              </div>
            ))}
            {authNeeded && !loading && (
              <div className="border border-amber-300 bg-amber-50 rounded p-4 text-sm text-amber-900">
                <div className="font-semibold mb-1">🔐 관리자 인증이 필요합니다</div>
                <div className="text-xs text-amber-800 mb-3">
                  분류 이력은 사용자 식별정보(user_id)를 포함해 관리자 토큰이 필요합니다.
                  토큰은 <b>12시간</b> 후 만료됩니다 — 아래에서 다시 인증하면 즉시 표시됩니다.
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="password"
                    value={adminPw}
                    onChange={(e) => setAdminPw(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') document.getElementById('adm-reauth-btn')?.click();
                    }}
                    placeholder="관리자 비밀번호"
                    className="border border-amber-300 rounded px-2 py-1 text-sm flex-1 max-w-[220px]"
                  />
                  <button
                    id="adm-reauth-btn"
                    disabled={adminAuthBusy || !adminPw.trim()}
                    onClick={async () => {
                      setAdminAuthBusy(true);
                      setAdminAuthErr('');
                      try {
                        const res = await fetch(`${apiBaseUrl}/api/auth/admin-login`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ password: adminPw }),
                        });
                        const d = await res.json();
                        if (d.success && d.token) {
                          localStorage.setItem('admin_token', d.token);
                          setAdminPw('');
                          setAuthNeeded(false);
                          fetchAll();
                        } else {
                          setAdminAuthErr(d.message || '인증 실패');
                        }
                      } catch (e2) {
                        setAdminAuthErr('서버 오류');
                      } finally {
                        setAdminAuthBusy(false);
                      }
                    }}
                    className="bg-amber-600 hover:bg-amber-700 text-white text-sm rounded px-3 py-1 disabled:opacity-50"
                  >
                    {adminAuthBusy ? '인증 중…' : '재인증'}
                  </button>
                </div>
                {adminAuthErr && (
                  <div className="text-xs text-rose-700 mt-2">{adminAuthErr}</div>
                )}
              </div>
            )}
            {!authNeeded && rows.length === 0 && !loading && (
              <div className="text-center text-gray-400 text-sm py-6">분류 이력 없음</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className={`text-2xl font-mono ${accent || 'text-gray-900'}`}>{value}</div>
    </div>
  );
}
